import React from 'react'

import type { BadgeProps } from '@wartek-id/badge'
import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text-v1'

import { usePelaksanaanPraktikKinerja } from 'app/KinerjaTimPenilaiPSPage/hooks/usePelaksanaanPraktikKinerja'

import type {
  StatusPelaksanaanPraktikKinerja,
  StatusPelaksanaanPraktikKinerjaMetadata,
} from 'types/kinerja-ps-pegawai'

export function PelaksanaanPraktikKinerjaStatusBadge({
  currentState,
  state,
  meta,
  disabled,
  done,
}: {
  currentState: StatusPelaksanaanPraktikKinerja
  state: StatusPelaksanaanPraktikKinerja
  meta?: StatusPelaksanaanPraktikKinerjaMetadata
  disabled?: boolean
  done?: boolean
}) {
  const {
    isDiskusiPersiapanFormSubmitted,
    isPendampinganSekolahFormSubmitted,
    isLoading,
  } = usePelaksanaanPraktikKinerja({ state: currentState })

  const badgeProps = React.useMemo((): {
    text: string
    variant: BadgeProps['variant']
  } => {
    if (disabled) {
      switch (state) {
        case 'DISKUSI_TINDAK_LANJUT':
          return {
            text: 'Nilai berdasarkan proses diskusi',
            variant: 'neutral',
          }
      }
    }

    if (done) {
      switch (state) {
        case 'DISKUSI_PERSIAPAN':
          return currentState === 'PENDAMPINGAN_SEKOLAH' &&
            !isPendampinganSekolahFormSubmitted
            ? {
                text: 'Cek isi catatan Pengawas',
                variant: 'informational',
              }
            : {
                text: 'Selesai',
                variant: 'success',
              }
        case 'PENDAMPINGAN_SEKOLAH':
          return currentState === 'DISKUSI_TINDAK_LANJUT'
            ? {
                text: 'Selesai. Survei otomatis dibagikan.',
                variant: 'success',
              }
            : { text: 'Selesai', variant: 'success' }
        case 'REFLEKSI_TINDAK_LANJUT_PENILAIAN':
          return {
            text: 'Nilai berdasarkan isi catatan.',
            variant: 'informational',
          }
        default:
          return { text: 'Selesai', variant: 'success' }
      }
    }

    switch (state) {
      case 'DISKUSI_PERSIAPAN':
        return isDiskusiPersiapanFormSubmitted
          ? {
              text: 'Cek isi catatan Pengawas',
              variant: 'informational',
            }
          : {
              text: 'Diskusikan bersama pengawas',
              variant: 'neutral',
            }
      case 'PENDAMPINGAN_SEKOLAH':
        return isPendampinganSekolahFormSubmitted
          ? {
              text: 'Selesai. Survei otomatis dibagikan.',
              variant: 'success',
            }
          : {
              text: 'Pantau pendampingan',
              variant: 'informational',
            }
      case 'DISKUSI_TINDAK_LANJUT':
        return meta?.[state]?.isPenilaian
          ? {
              text: 'Nilai berdasarkan proses diskusi',
              variant: 'informational',
            }
          : {
              text: 'Cek catatan pendampingan',
              variant: 'informational',
            }
      case 'DISKUSI_TINDAK_LANJUT_PENILAIAN':
        return {
          text: 'Selesai',
          variant: 'success',
        }
      case 'UPAYA_TINDAK_LANJUT':
        return {
          text: 'Cek isi upaya tindak lanjut',
          variant: 'informational',
        }
      case 'REFLEKSI_TINDAK_LANJUT':
        return {
          text: 'Nilai berdasarkan isi catatan',
          variant: 'informational',
        }
      case 'REFLEKSI_TINDAK_LANJUT_PENILAIAN':
        return {
          text: 'Nilai berdasarkan isi catatan.',
          variant: 'informational',
        }
      case 'SELESAI':
        return { text: 'Selesai', variant: 'success' }
    }
  }, [
    currentState,
    disabled,
    done,
    isDiskusiPersiapanFormSubmitted,
    isPendampinganSekolahFormSubmitted,
    meta,
    state,
  ])

  if (!badgeProps || isLoading) {
    return null
  }

  if (disabled) {
    return <Text variant="body-sm">{badgeProps.text}</Text>
  }

  return (
    <Badge variant={badgeProps.variant} className="w-fit">
      {badgeProps.text}
    </Badge>
  )
}

export default PelaksanaanPraktikKinerjaStatusBadge
